import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { FIXED_H } from './layoutConstants';
import TextStep from './TextStep';

// Progression rouge foncé → rouge → gris chaud → blanc → gris → rouge → rouge foncé (cycle)
const PALETTE = [
  { bg: '#7f1d1d', text: '#ffffff',  sub: 'rgba(255,255,255,0.55)', num: 'rgba(255,255,255,0.07)' },
  { bg: '#b91c1c', text: '#ffffff',  sub: 'rgba(255,255,255,0.60)', num: 'rgba(255,255,255,0.08)' },
  { bg: '#e7e5e4', text: '#1c1917',  sub: '#78716c',                num: 'rgba(0,0,0,0.05)'       },
  { bg: '#ffffff', text: '#1c1917',  sub: '#a8a29e',                num: 'rgba(0,0,0,0.04)'       },
];

const palette = (i) => PALETTE[i % PALETTE.length];

// En mobile, l'image épinglée occupe 60% de l'écran (h-[60vh]) : le déclenchement du texte doit
// être décalé sous cette zone, sinon il reste calé sur le centre de l'écran (caché sous l'image)
// et la bascule d'image semble arriver trop tard.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

const Gallery = ({ subtitle, images = [] }) => {
  const [active, setActive] = useState(0);
  const [sel,    setSel]    = useState(null);
  const isMobile = useIsMobile();
  const n = images.length;

  useEffect(() => { setActive(0); }, [n]);

  const open  = (i) => { setSel(i); document.body.style.overflow = 'hidden'; };
  const close = ()  => { setSel(null); document.body.style.overflow = ''; };
  const navLB = (d) => setSel(p => (p + d + n) % n);

  if (!n) return null;

  const img   = images[active];
  const color = palette(active);

  return (
    <>
      <div className="flex flex-col md:flex-row" style={{ '--fh': `${FIXED_H}px` }}>

        {/* ══ Image épinglée — mise en avant, le texte reste discret ═ */}
        <div
          className="relative z-20 w-full md:w-[72%] shrink-0 sticky md:self-start overflow-hidden cursor-pointer bg-stone-900 h-[60vh] md:h-[calc(100dvh-var(--fh))]"
          style={{ top: FIXED_H }}
          onClick={() => open(active)}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={`img-${active}`}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
            </motion.div>
          </AnimatePresence>

          {/* Fondu latéral vers le panneau texte */}
          <div
            className="absolute inset-0 pointer-events-none hidden md:block transition-colors duration-700"
            style={{ background: `linear-gradient(to right, transparent 55%, ${color.bg} 100%)` }}
          />

          {/* Bouton agrandir (mobile) */}
          <button
            onClick={e => { e.stopPropagation(); open(active); }}
            className="md:hidden absolute bottom-3 right-3 bg-black/50 text-white rounded-full px-3 py-1.5 text-xs font-medium backdrop-blur-sm flex items-center gap-1.5"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
            </svg>
            Agrandir
          </button>

          {/* Stepper vertical */}
          {n > 1 && (
            <div className="absolute right-6 md:right-8 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-3 transition-colors duration-700">
              <span className="text-sm font-bold font-mono tabular-nums transition-colors duration-700" style={{ color: color.text }}>
                {String(active + 1).padStart(2, '0')}
              </span>
              <div className="flex flex-col items-center gap-2.5">
                {images.map((_, i) => (
                  <div
                    key={i}
                    className="rounded-full transition-all duration-500"
                    style={{
                      width:           i === active ? '3px' : '2px',
                      height:          i === active ? '3.5rem' : '0.75rem',
                      backgroundColor: color.text,
                      opacity:         i === active ? 1 : 0.2,
                    }}
                  />
                ))}
              </div>
              <span className="text-sm font-mono tabular-nums transition-colors duration-700" style={{ color: color.sub }}>
                {String(n).padStart(2, '0')}
              </span>
            </div>
          )}
        </div>

        {/* ══ Texte — défile normalement, une couleur par photo ══════ */}
        <div className="flex-1">
          {/* Titre — fixe en haut de la colonne texte.
              Sur mobile, épinglé juste sous l'image (pas au même niveau qu'elle). */}
          <div
            className="sticky z-30 top-[calc(var(--fh)+60vh)] md:top-[var(--fh)] px-8 md:px-12 lg:px-16 py-6 transition-colors duration-700"
            style={{ backgroundColor: color.bg }}
          >
            <p className="text-[10px] font-semibold tracking-[0.25em] uppercase transition-colors duration-700" style={{ color: color.sub }}>
              {subtitle || 'Le club en images'}
            </p>
          </div>

          {images.map((im, i) => {
            const c = palette(i);
            return (
              <TextStep
                key={i}
                index={i}
                isActive={i === active}
                onActive={setActive}
                margin={isMobile ? '-62% 0px -18% 0px' : undefined}
                heightClass="min-h-[30vh] md:min-h-[calc(100dvh-var(--fh))]"
                padClass="pt-6 md:pt-0"
                className="relative px-6 md:px-8 lg:px-10 overflow-hidden"
                style={{ backgroundColor: c.bg }}
              >
                <div
                  className="absolute right-0 bottom-0 font-black leading-none select-none pointer-events-none"
                  style={{ fontSize: 'clamp(5rem, 12vw, 9rem)', color: c.num, lineHeight: 0.85 }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>

                <div className="relative z-10 space-y-2.5 md:space-y-4">
                  <h2 className="text-lg md:text-xl lg:text-2xl font-bold leading-tight" style={{ color: c.text }}>
                    {im.title || `Photo ${i + 1}`}
                  </h2>

                  {im.alt && (
                    <p className="text-xs md:text-sm leading-relaxed max-w-[16rem]" style={{ color: c.sub }}>
                      {im.alt}
                    </p>
                  )}

                  <button
                    onClick={() => open(i)}
                    className="inline-flex items-center gap-3 text-xs font-semibold transition-all duration-300 group"
                    style={{ color: c.sub }}
                  >
                    <span className="block h-px transition-all duration-300 group-hover:w-12 w-8" style={{ backgroundColor: 'currentColor' }} />
                    Voir en grand
                  </button>
                </div>
              </TextStep>
            );
          })}
        </div>

      </div>

      {/* ── Lightbox ──────────────────────────────────────── */}
      {sel !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={close}
        >
          <button onClick={close} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
            <XMarkIcon className="w-4 h-4" />
          </button>
          <button onClick={e => { e.stopPropagation(); navLB(-1); }} className="absolute left-2 md:left-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <button onClick={e => { e.stopPropagation(); navLB(1); }} className="absolute right-2 md:right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
            <ChevronRightIcon className="w-5 h-5" />
          </button>
          <div className="max-w-4xl px-14 w-full" onClick={e => e.stopPropagation()}>
            <img
              src={images[sel].src}
              alt={images[sel].alt}
              className="max-w-full max-h-[85vh] object-contain rounded-xl mx-auto"
            />
          </div>
          <div className="absolute bottom-5 flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={e => { e.stopPropagation(); setSel(i); }}
                className={`rounded-full transition-all ${i === sel ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/40 hover:bg-white/60'}`}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Gallery;
